import { useEffect, useRef } from 'react'
import { Link, useLocation, type To } from 'react-router-dom'

import { useApiClient } from '@/api/ApiClientProvider'
import { isNotLiveMethod } from '@/api/HttpApiClient'
import { Icon } from '@/design'
import { useCurrentTenant } from '@/lib/access'
import { useFilterSearch } from '@/lib/useGlobalFilters'

import { NOT_LIVE_SECTION_METHODS } from '../notLive'
import { ACCESS_UNKNOWN_TITLE, usePageAccess, type SidebarSection } from '../pageAccess'
import { activeTabFor, isInSection, SECTIONS, type SectionDef } from '../routes'
import { TenantMark } from './TenantMark'

export const APP_VERSION = __APP_VERSION__

/**
 * Primary navigation on the revised IA. A section the caller's role cannot
 * read is not shown; a section outside the tenant's plan is shown locked, so
 * the product's shape stays legible; one with no live page yet says "Not live
 * yet" (`NOT_LIVE_SECTION_METHODS`). A section whose module the plan gates
 * waits, inert, for the plan; if a read its first page needs fails — the plan,
 * or the session, when nothing is hidden for a role it cannot read — it says
 * "Unavailable" and leads to that page's retry state, neither locked nor
 * open. The active section expands to its tabs.
 * At tablet and phone widths the sidebar is a drawer (see shell.css).
 */
export function Sidebar({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { pathname } = useLocation()
  const search = useFilterSearch()
  const tenant = useCurrentTenant()
  const { session, navSections } = usePageAccess()
  const client = useApiClient()
  const closeRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (open) closeRef.current?.focus()
  }, [open])

  const ready = !session.isPending
  const entries = navSections(SECTIONS)
  const activeTab = activeTabFor(pathname)
  const isSectionNotLive = (section: SectionDef) => {
    const method = NOT_LIVE_SECTION_METHODS[section.key]
    return method ? isNotLiveMethod(client, method) : false
  }

  return (
    <aside id="ci-sidebar" className="ci-sidebar" data-open={open ? 'true' : 'false'} aria-label="Sidebar">
      <div className="ci-sidebar__brand">
        <TenantMark tenant={tenant.data} size={34} />
        <div className="ci-sidebar__names">
          <span className="ci-sidebar__tenant">{tenant.data?.name ?? ' '}</span>
          <span className="ci-sidebar__product">Competition Insights</span>
        </div>
        <button ref={closeRef} type="button" className="ci-sidebar__close ci-button" data-variant="ghost" onClick={onClose}>
          <Icon name="close" />
          <span className="ci-visually-hidden">Close navigation</span>
        </button>
      </div>

      <nav className="ci-nav" aria-label="Main">
        {ready ? (
          <ul>
            {entries.map(({ section, tabs, homePage, state, offersRetry }) => {
              const active = isInSection(section, pathname)
              const home = homePage?.path ?? section.prefix
              return (
                <li key={section.key} data-section={section.key}>
                  <SectionEntry
                    section={section}
                    state={state}
                    to={{ pathname: home, search }}
                    active={active}
                    current={active && tabs.length === 0}
                    notLive={isSectionNotLive(section)}
                    offersRetry={offersRetry}
                  />
                  {state === 'open' && active && tabs.length > 0 ? (
                    <ul className="ci-nav__sub">
                      {tabs.map((tab) => (
                        <li key={tab.path}>
                          <Link
                            to={{ pathname: tab.path, search }}
                            className="ci-nav__sublink"
                            aria-current={activeTab === tab.path ? 'page' : undefined}
                          >
                            {tab.label}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : (
          <p className="ci-visually-hidden" role="status">
            Loading navigation…
          </p>
        )}
      </nav>

      <footer className="ci-sidebar__footer">Powered by TmT Competition Insights · v{APP_VERSION}</footer>
    </aside>
  )
}

/**
 * One section's entry, by where the gate would land on its first page
 * (`PageState`): a link when it opens, or when a read it needs failed (to the
 * page's retry state, tagged "Unavailable"); inert while a read is under way;
 * locked when the plan is off.
 */
function SectionEntry({
  section,
  state,
  to,
  active,
  current,
  notLive,
  offersRetry,
}: {
  section: SectionDef
  state: SidebarSection['state']
  to: To
  active: boolean
  current: boolean
  notLive: boolean
  /** The page offers Try again (`sidebarSections`), so the hint may promise it. */
  offersRetry: boolean
}) {
  const label = (
    <>
      <Icon name={section.icon} size={18} />
      <span>{section.label}</span>
    </>
  )
  switch (state) {
    case 'pending':
      return (
        <span className="ci-nav__link" aria-disabled="true">
          {label}
          <span className="ci-visually-hidden"> (checking access)</span>
        </span>
      )
    case 'disabled':
      return (
        <span className="ci-nav__link" aria-disabled="true" title="Not included in your plan">
          {label}
          <Icon name="lock" size={14} className="ci-nav__lock" />
          <span className="ci-visually-hidden"> (not included in your plan)</span>
        </span>
      )
    case 'open':
    case 'unknown': {
      const unknown = state === 'unknown'
      return (
        <Link
          to={to}
          className="ci-nav__link"
          data-active={active ? 'true' : undefined}
          aria-current={current ? 'page' : undefined}
          title={unknown ? `${ACCESS_UNKNOWN_TITLE}.${offersRetry ? ' Open to try again.' : ''}` : undefined}
        >
          {label}
          {unknown ? (
            <>
              <span className="ci-nav__tag">Unavailable</span>
              <span className="ci-visually-hidden"> (access could not be confirmed)</span>
            </>
          ) : notLive ? (
            <span className="ci-nav__tag">Not live yet</span>
          ) : null}
        </Link>
      )
    }
  }
}
